// angular
import {Component, effect, inject} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {TranslateModule} from "@ngx-translate/core";
import {Router} from "@angular/router";
import {NgOptimizedImage} from "@angular/common";
// material angular
import {MatIconModule} from "@angular/material/icon";
import {MatFormFieldModule} from "@angular/material/form-field";
// components
import {InputComponent} from "@core/components/input/input.component";
import {ButtonComponent} from "@core/components/button/button.component";
// service
import {AuthService} from "@auth/shared/services/auth.service";
import {StorageService} from "@shared/services/storage.service";
import {LoadingService} from "@app/shared/services/loading.service";
import {MainConfigurationService} from "@app/shared/services/main-configuration.service";
// interfaces
import {IResendOtp} from "../shared/interfaces/signup.interface";
import {IForgotPass} from "../shared/interfaces/forgot.interface";
import {ConfirmPasswordValidator} from "@app/shared/validators/confirm-password.validator";
import {PreventFormSubmitDirective} from "@app/core/directives/form-enter-handler.directive";

@Component({
  selector: "app-forgot-password",
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    ButtonComponent,
    TranslateModule,
    NgOptimizedImage,
    MatIconModule,
    MatFormFieldModule,
    PreventFormSubmitDirective,
  ],
  templateUrl: "./forgot-password.component.html",
  styleUrl: "./forgot-password.component.scss",
})
export class ForgotPasswordComponent {
  forgotForm: FormGroup;
  forgotOtpForm: FormGroup;
  otpTimer = 120;
  showResendButton = true;
  isVerifyOtpBtnDisable = false;
  isPasswordVisible: boolean = false;
  isRepeatPasswordVisible: boolean = false;

  private _timerInterval: any;
  private _userInfo!: IResendOtp;

  // dependencies
  private readonly _fb = inject(FormBuilder);
  private readonly _storageService = inject(StorageService);
  private readonly _router = inject(Router);
  private readonly _loadingService = inject(LoadingService);
  public readonly authService = inject(AuthService);
  private readonly _mainConfigurationService = inject(MainConfigurationService);

  constructor() {
    this.forgotForm = this._fb.group({
      mobile: ["", Validators.required],
    });
    this.forgotOtpForm = this._fb.group(
      {
        password: ["", [Validators.required, Validators.minLength(8)]],
        password_confirmation: ["", Validators.required],
        otp: ["", Validators.required],
      },
      {validators: ConfirmPasswordValidator}
    );

    effect(() => {
      const userPhone = this.authService.userForgotPassState().mobile;

      if (userPhone) {
        this._userInfo = {
          ...userPhone,
        };
      }
    });
  }

  returnZero() {
    return 0;
  }

  ngOnInit() {
    this.startOtpTimer();
  }

  startOtpTimer() {
    this.otpTimer = 120;
    this.showResendButton = false;

    this._timerInterval = setInterval(() => {
      if (this.otpTimer > 0) {
        this.otpTimer--;
      } else {
        this.showResendButton = true;
        clearInterval(this._timerInterval);
      }
    }, 1000);
  }

  get formattedTimer() {
    const minutes = Math.floor(this.otpTimer / 60);
    const seconds = this.otpTimer % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  submit() {
    this.forgotForm.markAllAsTouched();
    if (this.forgotForm.invalid || !this.forgotForm.dirty) return;
    this._loadingService.setLoading(true);
    let params = this.forgotForm.value;
    this.authService
      .requestForgotPassword({mobile_number: params.mobile, prefix: "98"})
      .subscribe({
        next: (res) => {
          if (res) {
            this.authService.updateUserForgotPassState({
              state: 1,
              mobile: {mobile_number: params.mobile, prefix: "98"},
              hash: res.hash,
            });
            this._loadingService.setLoading(false);
          }
        },
        error: () => this._loadingService.setLoading(false),
        complete: () => this._loadingService.setLoading(false),
      });
  }

  submitOtp() {
    this.forgotOtpForm.markAllAsTouched();
    if (this.forgotOtpForm.invalid || !this.forgotOtpForm.dirty) return;
    this._loadingService.setLoading(true);
    const params: IForgotPass = {
      ...this.forgotOtpForm.value,
      otp: Number(this.forgotOtpForm.controls["otp"].value),
      hash: this.authService.userForgotPassState().hash,
      mobile: this.authService.userForgotPassState().mobile,
    };

    this.isVerifyOtpBtnDisable = true;

    this.authService.forgotPassword(params).subscribe({
      next: (res) => {
        if (res && res.success) {
          this._storageService.setToken(res.access_token);
          this._storageService.setRefreshToken(res.refresh_token);
          this._mainConfigurationService.init().then(() => {
            this._router.navigate(["/"]);
            this._loadingService.setLoading(false);
          });
        }
      },
      error: (err) => {
        this.isVerifyOtpBtnDisable = false;
        this._loadingService.setLoading(false);
      },
      complete: () => this._loadingService.setLoading(false),
    });
  }

  resendOtp() {
    if (this._userInfo) {
      this.startOtpTimer();

      this.authService.resendOtp({...this._userInfo}).subscribe({
        next: (res) => {
          this.authService.updateUserForgotPassState({
            hash: res.hash,
          });
        },
        error: (error) => {},
      });
    }
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }
  toggleRepeatPasswordVisibility(): void {
    this.isRepeatPasswordVisible = !this.isRepeatPasswordVisible;
  }

  ngOnDestroy() {
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
    }
  }
}
